// Small deterministic ground planner. Obstacles include an allowance for the
// character's shoulders, so a route never merely clears a mesh by its centre.
export function blocked(x, z, obstacles, limit = 11.5) {
  if (Math.hypot(x, z) > limit) return true;
  return obstacles.some((item) => {
    if (item.type === 'box') return x >= item.x0 && x <= item.x1 && z >= item.z0 && z <= item.z1;
    if (item.type === 'oriented-box') {
      const dx = x - item.x, dz = z - item.z;
      const cos = Math.cos(item.angle), sin = Math.sin(item.angle);
      const localX = cos * dx - sin * dz;
      const localZ = sin * dx + cos * dz;
      return Math.abs(localX) <= item.halfWidth && Math.abs(localZ) <= item.halfDepth;
    }
    return Math.hypot(x - item.x, z - item.z) <= item.r;
  });
}

function boxSegment(a, b, halfWidth, halfDepth) {
  let enter = 0, exit = 1;
  for (const [axis, half] of [['x',halfWidth],['z',halfDepth]]) {
    const delta = b[axis] - a[axis];
    if (Math.abs(delta) < 1e-12) { if (Math.abs(a[axis]) > half) return false; continue; }
    let first = (-half - a[axis]) / delta, last = (half - a[axis]) / delta;
    if (first > last) { const swap=first; first=last; last=swap; }
    enter=Math.max(enter,first); exit=Math.min(exit,last);
    if (enter > exit) return false;
  }
  return true;
}

export function clearSegment(a, b, obstacles, limit = 11.5) {
  if (blocked(a.x,a.z,obstacles,limit) || blocked(b.x,b.z,obstacles,limit)) return false;
  // The radial boundary is convex; inside endpoints imply an inside segment.
  // Analytical footprint intersections also catch props smaller than a sample.
  const dx=b.x-a.x, dz=b.z-a.z, lengthSq=dx*dx+dz*dz;
  for (const item of obstacles) {
    if (item.type === 'box') {
      const x=(item.x0+item.x1)/2,z=(item.z0+item.z1)/2;
      if (boxSegment({x:a.x-x,z:a.z-z},{x:b.x-x,z:b.z-z},(item.x1-item.x0)/2,(item.z1-item.z0)/2)) return false;
    } else if (item.type === 'oriented-box') {
      const cos=Math.cos(item.angle),sin=Math.sin(item.angle);
      const local=p=>({x:cos*(p.x-item.x)-sin*(p.z-item.z),z:sin*(p.x-item.x)+cos*(p.z-item.z)});
      if (boxSegment(local(a),local(b),item.halfWidth,item.halfDepth)) return false;
    } else {
      const t=lengthSq ? Math.max(0,Math.min(1,((item.x-a.x)*dx+(item.z-a.z)*dz)/lengthSq)) : 0;
      if (Math.hypot(a.x+dx*t-item.x,a.z+dz*t-item.z) <= item.r) return false;
    }
  }
  return true;
}

export function planWalkRoute(start, destination, obstacles, limit = 11.5) {
  // A blocked source must be repaired by collision/restore policy first. A
  // nearest free grid cell is not permission to cross its containing obstacle.
  if (blocked(start.x,start.z,obstacles,limit)) return [];
  const length = Math.hypot(destination.x, destination.z);
  const goal = length > limit - .3
    ? { x: destination.x * (limit - .3) / length, z: destination.z * (limit - .3) / length }
    : { x: destination.x, z: destination.z };
  if (clearSegment(start, goal, obstacles, limit)) return [goal];
  const step = .4, extent = Math.ceil(limit / step), width = extent * 2 + 1;
  const id = (i, j) => j * width + i;
  const point = (i, j) => ({ x: (i - extent) * step, z: (j - extent) * step });
  const closest = (target, connectFrom = null) => {
    let best = null, bestDist = Infinity;
    for (let j = 0; j < width; j++) for (let i = 0; i < width; i++) {
      const p = point(i, j);
      if (blocked(p.x, p.z, obstacles, limit)) continue;
      const distance = Math.hypot(p.x - target.x, p.z - target.z);
      if (distance < bestDist && (!connectFrom || clearSegment(connectFrom,p,obstacles,limit))) { best = { i, j, p }; bestDist = distance; }
    }
    return best;
  };
  const source = closest(start,start), target = closest(goal);
  if (!source || !target) return [];
  const destinationId = id(target.i, target.j);
  // Float32 rounding can make an equal-cost diagonal look cheaper on every
  // revisit, repeatedly requeueing the same cell during a long detour.
  const cost = new Float64Array(width * width).fill(Infinity);
  const parent = new Int32Array(width * width).fill(-1);
  const open = [{ i: source.i, j: source.j, score: 0, cost: 0 }];
  cost[id(source.i, source.j)] = 0;
  while (open.length) {
    open.sort((a, b) => b.score - a.score);
    const current = open.pop();
    const currentId = id(current.i, current.j);
    if (current.cost > cost[currentId] + 1e-9) continue;
    if (currentId === destinationId) break;
    const from = point(current.i, current.j);
    for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
      if (!di && !dj) continue;
      const i = current.i + di, j = current.j + dj;
      if (i < 0 || j < 0 || i >= width || j >= width) continue;
      const to = point(i, j);
      if (!clearSegment(from, to, obstacles, limit)) continue;
      const nextId = id(i, j), nextCost = cost[currentId] + Math.hypot(di, dj);
      if (nextCost >= cost[nextId] - 1e-9) continue;
      cost[nextId] = nextCost;
      parent[nextId] = currentId;
      open.push({ i, j, cost: nextCost, score: nextCost + Math.hypot(to.x - target.p.x, to.z - target.p.z) / step });
    }
  }
  if (!Number.isFinite(cost[destinationId])) return [];
  const cells = [];
  for (let cursor = destinationId; cursor !== -1; cursor = parent[cursor]) {
    cells.push(point(cursor % width, Math.floor(cursor / width)));
  }
  cells.reverse();
  const route = [];
  let from = start;
  for (let i = 0; i < cells.length; i++) {
    if (Math.hypot(cells[i].x-from.x,cells[i].z-from.z)<1e-8) continue;
    if (i < cells.length - 1 && clearSegment(from, cells[i + 1], obstacles, limit)) continue;
    if (!clearSegment(from,cells[i],obstacles,limit)) return [];
    route.push(cells[i]); from = cells[i];
  }
  if (!blocked(goal.x, goal.z, obstacles, limit) && clearSegment(from, goal, obstacles, limit)) route.push(goal);
  return route.length ? route : [target.p];
}
